import { useState, FC } from "react";
import { Box } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HomeIcon from "src/Pages/Company/OrganizationStructure/assets/icons/HomeIcon";
import useInitialExpandState from "src/Pages/Company/OrganizationStructure/Areas/Components/EmployeeHierarchy/utils/useInitialExpandState";

export interface TreeNodeData {
  areaId: string | null;
  name: string;
  children?: TreeNodeData[];
}

type TreeNodeProps = {
  node: TreeNodeData;
  areaId: string | null;
  initialExpandState?: unknown[];
};

export interface FlatNode {
  areaId: string;
  name: string;
  parentId: string | null;
  hasChildren: boolean;
}

const TreeNode: FC<TreeNodeProps> = ({ node, areaId, initialExpandState }) => {
  const isCurrentNode = node.areaId === String(areaId);

  const [expanded, setExpanded] = useState(
    !!initialExpandState?.find((route) => route === node.areaId) ||
      node.areaId === null
  );
  const hasChildren = node.children && node.children.length > 0;

  const toggleExpanded = () => {
    if (hasChildren) setExpanded(!expanded);
  };

  const style = {
    node: {
      display: "flex",
      alignItems: "center",
      userSelect: "none",
      padding: 0,
      color: "inherit",
      fontFamily: "inherit",
      fontSize: "16px",
      fontWeight: 700,
    },
    arrow: {
      padding: "6px 0 6px 6px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transform: expanded ? "rotate(360deg)" : "rotate(270deg)",
      transition: "transform 0.3s ease",
      opacity: hasChildren ? 1 : 0,
      cursor: hasChildren ? "pointer" : "default",
    },
    childContainer: {
      overflow: "hidden",
      transition: "max-height 0.3s ease, opacity 0.3s ease",
      maxHeight: expanded ? "1000px" : "0px",
      opacity: expanded ? 1 : 0,
      alignItems: "center",
      marginLeft: "32px",
    },
    iconAfterText: {
      paddingLeft: "8px",
      paddingTop: "6px",
      cursor: "pointer",
    },
    text: {
      cursor: hasChildren ? "pointer" : "default",
      paddingLeft: "16px",
    },
  };

  return (
    <>
      <Box sx={{ ...style.node, color: isCurrentNode ? "#036BAE" : "inherit" }}>
        <Box component="span" sx={style.arrow} onClick={toggleExpanded}>
          <ArrowDropDownIcon />
        </Box>
        <Box component="span" sx={style.text} onClick={toggleExpanded}>
          {node.name}
        </Box>
        {isCurrentNode && (
          <Box
            component="span"
            sx={style.iconAfterText}
            onClick={toggleExpanded}
          >
            <Box>
              <HomeIcon />
            </Box>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          ...style.childContainer,
          maxHeight: expanded ? "1000px" : "0px",
          opacity: expanded ? 1 : 0,
        }}
      >
        {hasChildren &&
          node.children?.map((child) => (
            <TreeNode
              key={child.areaId}
              node={child}
              areaId={areaId}
              initialExpandState={initialExpandState}
            />
          ))}
      </Box>
    </>
  );
};

type TreeProps = {
  data: TreeNodeData[];
  areaId: string | null;
};

const Tree: FC<TreeProps> = ({ data, areaId }) => {
  const initialExpandState = useInitialExpandState({
    data,
    areaId: areaId || "",
  });

  return (
    <>
      {data.map((node) => (
        <TreeNode
          key={node.areaId}
          node={node}
          areaId={areaId}
          initialExpandState={initialExpandState}
        />
      ))}
    </>
  );
};

type EmployeeHierarchyProps = {
  treeData: TreeNodeData[];
  areaId: string | null;
};

const EmployeeHierarchy: FC<EmployeeHierarchyProps> = ({
  treeData,
  areaId,
}) => <Tree data={treeData} areaId={areaId} />;

export default EmployeeHierarchy;
